/**
 * THE KICKOFF ROSTER AND THE ONE SPAWN. (§8.2, §8.3, §9.2)
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * Until now there were TWO unrelated copies of the same kickoff table — one in
 * `packages/server/src/room.ts`, one in `packages/client/src/game/practice.ts` —
 * each with its own `ROSTERS`, its own `SPAWN_ANCHORS`, its own spawn loop, and
 * each saying in its own comment that the other existed and that "if a roster
 * ever lands in MatchConfig, both copies die". It has, so they have. Both now
 * call `spawnSquads` below, so offline practice and the authoritative server
 * cannot drift apart in how a match begins.
 *
 * ── The default layout, and what it is NOT ──────────────────────────────────
 *
 * `DEFAULT_ROSTERS` / `KICKOFF_SPAWN_ANCHORS` are the two tables that used to be
 * duplicated, moved here VERBATIM. They are a SPAWN LAYOUT, not a formation:
 * their only job is that 22 players do not start stacked on the centre spot.
 * §10.1's Layer 3 owns the real shape and walks everyone into it during the
 * kickoff setup window (§5.1), which is why these numbers stop mattering a
 * couple of seconds in and cannot decide a match.
 *
 * They are deliberately NOT `sim/formation-anchors.ts`'s `POSITION_ANCHORS`,
 * even though the two tables are cousins. POSITION_ANCHORS is the AI's *at rest*
 * mid-block and the rules FSM's restart staging, and it is retuned when the
 * block is retuned (it moved substantially on 2026-07-25). Rebasing the spawn on
 * it would silently change where every existing mode kicks off from — a change
 * nobody asked for, bundled into a change about picking your XI. Keeping the
 * shipped numbers here is what makes "an ABSENT roster reproduces today's
 * kickoff exactly" a fact rather than a hope; roster.test.ts asserts it.
 *
 * ── The slot identity ───────────────────────────────────────────────────────
 *
 *     PlayerId = team * squadSize + slot
 *
 * That is not new — both old copies computed exactly this — but it is now the
 * documented contract that lets a front end carry an XI as "slot → my player id"
 * and get the man it picked in the shirt it picked (types.ts `TeamRoster`).
 */

import {
  type MatchConfig,
  type Position,
  type WorldState,
  MAX_PLAYERS,
} from './types.js';
import { spawnPlayer } from './world.js';

/**
 * Default kickoff roster per squad size. 11v11 is a 4-4-2; the smaller modes are
 * the same shape with fewer of the same positions (§9.2 — one codebase, all
 * modes). Used whenever `MatchConfig.roster` is absent or unusable.
 */
export const DEFAULT_ROSTERS: Record<number, readonly Position[]> = {
  1: ['ST'],
  2: ['GK', 'ST'],
  3: ['GK', 'CB', 'ST'],
  4: ['GK', 'CB', 'CM', 'ST'],
  5: ['GK', 'CB', 'CB', 'CM', 'ST'],
  7: ['GK', 'CB', 'CB', 'CM', 'LM', 'RM', 'ST'],
  11: ['GK', 'LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'CM', 'RM', 'ST', 'ST'],
};

/**
 * Where a position stands at kickoff, normalized: fx along the team's own attack
 * axis (-1 = own goal line, +1 = opponent's), fz across (sign is the team's own
 * left/right, mirrored into world space per side). See the header for why this
 * is not POSITION_ANCHORS.
 */
export const KICKOFF_SPAWN_ANCHORS: Record<Position, { fx: number; fz: number }> = {
  GK: { fx: -0.94, fz: 0 },
  CB: { fx: -0.72, fz: 0 },
  LB: { fx: -0.6, fz: -0.72 },
  RB: { fx: -0.6, fz: 0.72 },
  LWB: { fx: -0.42, fz: -0.85 },
  RWB: { fx: -0.42, fz: 0.85 },
  CDM: { fx: -0.42, fz: 0 },
  CM: { fx: -0.12, fz: 0 },
  CAM: { fx: 0.18, fz: 0 },
  LM: { fx: -0.1, fz: -0.78 },
  RM: { fx: -0.1, fz: 0.78 },
  LW: { fx: 0.32, fz: -0.8 },
  RW: { fx: 0.32, fz: 0.8 },
  CF: { fx: 0.44, fz: 0 },
  ST: { fx: 0.6, fz: 0 },
};

/** Lateral spread applied to the Nth player sharing a central position, so two
 *  CBs are two CBs and not one CB drawn twice. Fraction of the half-width. */
export const SPAWN_DUPLICATE_SPREAD = 0.22;

/** The default layout for a squad size, with the same 5-a-side fallback both old
 *  copies used for a size nobody tabulated. */
export function defaultRosterSlots(squadSize: number): readonly Position[] {
  return DEFAULT_ROSTERS[squadSize] ?? DEFAULT_ROSTERS[5]!;
}

/**
 * The position list a team actually takes the field in.
 *
 * The chosen XI wins ONLY if it is usable: right length for the squad size and
 * every entry a real §8.2 code. Anything else falls back to the default layout
 * for the whole team rather than to a half-applied hybrid — a config that half
 * arrives is a bug to see at kickoff, not a lineup to guess at. `squadSize` is
 * the authority on length because it is what decides how many slots exist and
 * how PlayerIds are numbered.
 */
export function rosterSlotsFor(config: MatchConfig, team: 0 | 1): readonly Position[] {
  const fallback = defaultRosterSlots(config.squadSize);
  const chosen = config.roster?.teams[team];
  if (!chosen) return fallback;
  const slots = chosen.slots;
  if (slots.length !== config.squadSize) return fallback;
  for (let i = 0; i < slots.length; i++) {
    if (KICKOFF_SPAWN_ANCHORS[slots[i]!] === undefined) return fallback;
  }
  return slots;
}

/**
 * Put both squads on the pitch. §9.2: pitch size, rules and formations are all
 * data-driven from MatchConfig, so this is one function for every mode — offline
 * practice, the authoritative server, and every test harness.
 *
 * Deterministic and RNG-free: it reads config and `teamAAttacksPositiveX` and
 * writes positions. No draw, no clock, nothing per-tick (§15.3).
 */
export function spawnSquads(w: WorldState): void {
  const size = w.config.squadSize;
  const halfLength = w.config.pitchLength * 0.5;
  const halfWidth = w.config.pitchWidth * 0.5;

  for (let team = 0 as 0 | 1; team <= 1; team = (team + 1) as 0 | 1) {
    const roster = rosterSlotsFor(w.config, team);
    const seen = new Map<Position, number>();
    // Same sign rule as rules/fsm.ts attackDir, inlined so sim/roster.ts does
    // not have to depend on the rules layer for two comparisons.
    const dir = (team === 0) === w.teamAAttacksPositiveX ? 1 : -1;

    for (let slot = 0; slot < size; slot++) {
      const pos = roster[slot] ?? 'CM';
      const id = team * size + slot;
      if (id >= MAX_PLAYERS) break;
      spawnPlayer(w, id, team, pos);

      const anchor = KICKOFF_SPAWN_ANCHORS[pos];
      const dupe = seen.get(pos) ?? 0;
      seen.set(pos, dupe + 1);

      // Map fx ∈ [-1,+1] onto the team's OWN half: at a kickoff everyone must be
      // behind the halfway line (Law 8), so an ST's +0.6 becomes "just behind
      // halfway", not "in the opponent's box".
      const ownHalfFx = (anchor.fx - 1) * 0.5;
      // Alternate duplicates left/right of their anchor: 0 → 0, 1 → +s, 2 → -s.
      const spread =
        dupe === 0 ? 0 : (dupe % 2 === 1 ? 1 : -1) * Math.ceil(dupe / 2) * SPAWN_DUPLICATE_SPREAD;

      w.players.px[id] = dir * ownHalfFx * halfLength;
      w.players.py[id] = 0;
      w.players.pz[id] = dir * (anchor.fz + spread) * halfWidth;
      // Face the opponent's goal.
      w.players.yaw[id] = dir > 0 ? 0 : Math.PI;
    }
  }
}
